import React, { FC, useMemo, useState } from 'react'
import { useApolloClient, useMutation, useQuery } from 'react-apollo'
import { toast } from 'react-toastify'
import { CardNumberElement, Elements, useElements, useStripe } from '@stripe/react-stripe-js'
import { loadStripe } from '@stripe/stripe-js'
import { useUserInfo } from 'src/services'
import { PW_GET_CELLULAR_UPCOMING, PW_GET_SETUP_INTENT } from '../../../apollo/api/paywall/queries'
import { PW_SET_UP_CELLULAR_AUTO_PAY } from '../../../apollo/api/paywall/mutations'
import { isHideFeature } from '../../../helpers/white-label-helper'
import { WhiteLabelFeaturesEnum } from '../../../helpers/white-label.enum'
import { CellularCardForm, CardFormValue, EMPTY_CARD, isCardReady } from './cellular-card-form'
import { PromptButton, PromptDialog, PromptFooter, PromptHeader, SummaryBox } from './dialog-parts'
import { CreditCardIcon } from './icons'
import { formatDate } from './format'
import { useCellularPayPromptActive } from './prompt-active'
import { isUpcomingSnoozed, snoozeUpcoming } from './upcoming-snooze'
import { logInDev } from './log'

const TITLE_ID = 'cellular-upcoming-title'
const DESCRIPTION_ID = 'cellular-upcoming-description'

/** $240 a year per controller: what the first bill charges (report 23 §6.7). */
export const YEARLY_PRICE_USD = 240

export const AUTO_PAY_SAVED = 'Auto-pay is set up. We will charge this card the day after your included service ends.'
const SAVE_FAILED = 'We could not save this card. Please try again, or wait for the bill by email.'

export interface CellularUpcoming {
  controllers: string[]
  endsOn: string | null
  autoPay: boolean
}

export const upcomingDescription = (upcoming: CellularUpcoming): string => {
  const ends = formatDate(upcoming.endsOn)
  const list = upcoming.controllers.join(', ')
  return (
    `The cellular service included with ${list} ends${ends ? ` on ${ends}` : ' soon'}. ` +
    `After that it is $${YEARLY_PRICE_USD} a year per controller. We will email the bill the day ` +
    'after, with 14 days to pay — or save a card now and we will charge it then.'
  )
}

const publishableKey = (): string | undefined => process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY

const UpcomingBody: FC<{
  accountId: string
  upcoming: CellularUpcoming
  onDone: () => void
  onNotNow: () => void
}> = ({ accountId, upcoming, onDone, onNotNow }) => {
  const stripe = useStripe()
  const elements = useElements()
  const [card, setCard] = useState<CardFormValue>(EMPTY_CARD)
  const [saving, setSaving] = useState(false)
  const [failure, setFailure] = useState<string | null>(null)
  const client = useApolloClient()
  const [setUpAutoPay] = useMutation(PW_SET_UP_CELLULAR_AUTO_PAY)

  const save = async () => {
    const element = elements?.getElement(CardNumberElement)
    if (!stripe || !element) {
      setFailure('The card form is still loading. Please try again in a moment.')
      return
    }
    setSaving(true)
    setFailure(null)
    let saved = false
    try {
      // Review item 17: the SetupIntent is made only when the owner presses
      // "Set up auto-pay", never just because the prompt showed.
      const { data: intent } = await client.query({
        query: PW_GET_SETUP_INTENT,
        variables: { accountId },
        fetchPolicy: 'network-only'
      })
      const clientSecret: string | undefined =
        intent?.getCardSetupIntentByAccountId?.client_secret // eslint-disable-line camelcase
      if (!clientSecret) {
        setFailure(SAVE_FAILED)
        return
      }
      const { error, setupIntent } = await stripe.confirmCardSetup(clientSecret, {
        payment_method: { card: element, billing_details: { name: card.name.trim() } } // eslint-disable-line camelcase
      })
      if (error || !setupIntent || setupIntent.status !== 'succeeded') {
        setFailure(error?.message || SAVE_FAILED)
        return
      }
      const { data } = await setUpAutoPay({
        variables: { accountId, paymentMethodId: setupIntent.payment_method as string }
      })
      if (!data?.setUpCellularAutoPay) {
        setFailure(SAVE_FAILED)
        return
      }
      toast.success(AUTO_PAY_SAVED)
      saved = true
      onDone()
    } catch (error) {
      logInDev('Cellular auto-pay not saved', error)
      setFailure(SAVE_FAILED)
    } finally {
      // After a save the parent closes and unmounts this body: no state update then.
      if (!saved) setSaving(false)
    }
  }

  return (
    <>
      <PromptHeader
        descriptionId={DESCRIPTION_ID}
        icon={<CreditCardIcon />}
        id={TITLE_ID}
        title='Your included cellular service ends soon'
        tone='primary'
      >
        {upcomingDescription(upcoming)}
      </PromptHeader>
      <SummaryBox>
        <CellularCardForm
          disabled={saving}
          onChange={update => setCard(update)}
          value={card}
        />
      </SummaryBox>
      {failure && (
        <p
          data-testid='cellular-upcoming-failure'
          role='alert'
        >
          {failure}
        </p>
      )}
      <PromptFooter>
        <PromptButton
          disabled={saving}
          onClick={onNotNow}
          variant='outline'
        >
          Not now
        </PromptButton>
        <PromptButton
          disabled={saving || !isCardReady(card)}
          onClick={save}
          variant='primary'
        >
          {saving ? 'Saving…' : 'Set up auto-pay'}
        </PromptButton>
      </PromptFooter>
    </>
  )
}

/**
 * SHA-973 P4c (E8, Q4; P3 decision 8): the owner's controllers whose included cellular
 * ends soon (their T-30 notice went) and that nothing bills yet. "Set up auto-pay"
 * saves a card through a SetupIntent as the account's cellular auto-pay method: the
 * billing engine charges the first bill on it the day after the service ends. "Not
 * now" snoozes it until a week before (the T-7 notice). It never shows while the
 * pay prompt has a bill on screen, after auto-pay is set, to members (the paywall
 * answers null), or to white-label tenants. "Pay now" is not in P4c (decision 7).
 */
export const CellularUpcomingPrompt: FC = () => {
  const userInfo = useUserInfo()
  const accountId: string | undefined = userInfo?.me?.account?.id
  const skip = !accountId || isHideFeature(WhiteLabelFeaturesEnum.PAYWALL)
  const payPromptActive = useCellularPayPromptActive()
  const { data, refetch } = useQuery(PW_GET_CELLULAR_UPCOMING, {
    skip,
    variables: { accountId },
    fetchPolicy: 'network-only'
  })
  const [closed, setClosed] = useState(false)
  const key = publishableKey()
  const stripePromise = useMemo(() => (key ? loadStripe(key) : null), [key])
  const upcoming: CellularUpcoming | null | undefined = skip ? null : data?.getCellularUpcoming

  if (
    skip ||
    !accountId ||
    !upcoming ||
    upcoming.autoPay ||
    !upcoming.controllers.length ||
    closed ||
    payPromptActive ||
    !key ||
    isUpcomingSnoozed(upcoming.endsOn)
  ) {
    return null
  }

  const notNow = () => {
    if (upcoming.endsOn) snoozeUpcoming(upcoming.endsOn)
    setClosed(true)
  }
  const done = () => {
    setClosed(true)
    refetch().catch(error => logInDev('Failed to refresh upcoming cellular', error))
  }

  return (
    <PromptDialog
      describedBy={DESCRIPTION_ID}
      labelledBy={TITLE_ID}
      open
    >
      <Elements stripe={stripePromise}>
        <UpcomingBody
          accountId={accountId}
          onDone={done}
          onNotNow={notNow}
          upcoming={upcoming}
        />
      </Elements>
    </PromptDialog>
  )
}
